import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatInputModule } from "@angular/material/input";
import { SystemAdminService } from "../../../services/system-admin.service";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../../utils/no-whitespace.validator';
import { ToastService } from "../../../services/toast.service";
import { ToastType } from "../../../enums/config.enum";

@Component({
    selector: 'app-mailkit',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatInputModule, ReactiveFormsModule],
    templateUrl: './mailkit.component.html',
    styleUrl: './mailkit.component.scss'
})
export class MailkitComponent implements OnInit {
    systemAdminService = inject(SystemAdminService);
    isMailConfigUpdate = signal<boolean>(false);
    configId = signal<number>(0);
    isReadOnly = signal<boolean>(true);
    saveSuccess = signal<boolean>(false);
    errorMessage = signal<string | null>(null);
    toastService = inject(ToastService);
    fb: FormBuilder = inject(FormBuilder);
    mailKitForm = this.fb.group({
        smtpServer: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        smtpPort: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        // userName: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        // password: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        // useSSL: [null, [Validators.required, noWhitespaceValidator]],
        // useTLS: [null, [Validators.required, noWhitespaceValidator]],
        protocol : [1 ,[Validators.required, noWhitespaceValidator]],
        bccEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        createUserFromEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(64)]],
        createUserFromName: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(64)]],
        createUserSubject: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        createUserMailBody: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(256)]],
        // userCreationExpMins: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        forgotUserFromEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        forgotUserFromName: [null, [Validators.required, noWhitespaceValidator]],
        forgotUserSubject: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        forgotUserMailBody: [null, [Validators.required, noWhitespaceValidator]],
        expiryMailFromEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        expiryMailFromName: [null, [Validators.required, noWhitespaceValidator]],
        expiryMailSubject: [null, [Validators.required, noWhitespaceValidator]],
        expiryMailBody: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(256)]],
        renewalMailFromEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        renewalMailFromName: [null, [Validators.required, noWhitespaceValidator]],
        renewalMailSubject: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        renewalMailBody: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(256)]],
        messageToEmail: [null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        messageMailSubject: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(256)]],
        messageMailBody: [null, [Validators.required, noWhitespaceValidator, Validators.maxLength(1024)]],
        messageFromEmail:[null, [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
        resetExpiryInMins: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
        mailExpiryInDays: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[0-9]+$')]],
    });

    ngOnInit(): void {
        this.getMailConfigDetails();
    }

    getMailConfigDetails() {
        this.systemAdminService.getMailConfig().subscribe({
            next: (response: any) => {
                if (response && response.length > 0) {
                    response.forEach((mailConfig: any) => {
                        // Process the single object in the response
                        this.mailKitForm.patchValue({
                            smtpServer: mailConfig.smtpServer,
                            smtpPort: mailConfig.smtpPort,
                            // userName: mailConfig.username,
                            // password: mailConfig.password,
                            bccEmail: mailConfig.bccEmail,
                            createUserFromEmail: mailConfig.createUserFromEmail,
                            createUserFromName: mailConfig.createUserFromName,
                            createUserSubject: mailConfig.createUserSubject,
                            createUserMailBody: mailConfig.createUserMailBody,
                            forgotUserFromEmail: mailConfig.forgotUserFromEmail,
                            forgotUserFromName: mailConfig.forgotUserFromName,
                            forgotUserSubject: mailConfig.forgotUserSubject,
                            forgotUserMailBody: mailConfig.forgotUserMailBody,
                            expiryMailFromEmail: mailConfig.expiryMailFromEmail,
                            expiryMailFromName: mailConfig.expiryMailFromName,
                            expiryMailSubject: mailConfig.expiryMailSubject,
                            expiryMailBody: mailConfig.expiryMailBody,
                            renewalMailFromEmail: mailConfig.renewalMailFromEmail,
                            renewalMailFromName: mailConfig.renewalMailFromName,
                            renewalMailSubject: mailConfig.renewalMailSubject,
                            renewalMailBody: mailConfig.renewalMailBody,
                            messageMailSubject: mailConfig.messageMailSubject,
                            messageToEmail: mailConfig.messageToEmail,
                            messageMailBody: mailConfig.messageMailBody,
                            messageFromEmail: mailConfig.messageFromEmail,
                            // userCreationExpMins: mailConfig.userCreationExpMins,

                            resetExpiryInMins: mailConfig.resetExpiryInMins,
                            mailExpiryInDays: mailConfig.mailExpiryInDays,
                            // useSSL: mailConfig.useSSL,
                            // useTLS: mailConfig.useTLS,
                            protocol: mailConfig.useTLS ? 1 : 0,
                        });

                        this.configId.set(mailConfig.configId);
                        this.isMailConfigUpdate.set(true);
                    });
                } else {
                    console.error('Response is invalid or empty:', response);
                }
            },
            error: (error: any) => {
                console.error('Error fetching mail config details:', error);
            }
        });
    }



    saveMailConfig(payload: any) {
        this.systemAdminService.saveMailConfig(payload).subscribe({
            next: (response: any) => {
                if (response) {
                    this.toastService.addNotification({message: "Mail config created", type: ToastType.Success});
                    // if response is response redirect show success prompt
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    updateMailConfig(configId: number, payload: any) {
        this.systemAdminService.updateMailConfig(configId, payload).subscribe({
            next: (response: any) => {
                if (response) {
                    this.isReadOnly.set(true);
                    this.toastService.addNotification({message: "Mail config updated", type: ToastType.Success});
                    // if response is response redirect show success prompt
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    onMailConfigSubmit() {
     
        if (this.mailKitForm.invalid) {
            this.mailKitForm.markAllAsTouched();
            return;
        }
        const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const userId = userDetails.userId;

        const payload = this.mailKitForm.getRawValue() as any;
        payload.createdUserId = userId;
        payload.updatedUserId = userId;
        payload.createdDate = new Date().toISOString();
        payload.updatedDate = new Date().toISOString();
        payload.useSSL = payload.protocol ? 0 : 1;
        payload.useTLS = payload.protocol ? 1 : 0;

        this.isMailConfigUpdate() ? this.updateMailConfig(this.configId(), payload) : this.saveMailConfig(payload);
    }

    toggleReadOnly(){
        this.isReadOnly.set(!this.isReadOnly());
    }

    cancelClick(){
        this.getMailConfigDetails();
        this.isReadOnly.set(true);
    }

}